"use client";

import { useCallback, useId, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { BookOpen, LayoutDashboard, Target } from "lucide-react";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const STEPS = [
  {
    key: 0,
    label: "Subscribe",
    /** Short line on the step chip only */
    summary: "Commercial scope: seats, term, and success measures.",
    title: "Your org subscribes and we scope the program",
    body: "We align the subscription to your audience, outcomes, and how you will measure success at renewal—seats, term, and clear criteria, not a generic library purchase.",
    insight: "Procurement and L&D share one scope: who is covered, for how long, and what “good” looks like before anyone logs in.",
    icon: Target,
  },
  {
    key: 1,
    label: "Course",
    summary: "We build the course your subscription funds—personalized for every user.",
    title: "We generate the course for your organization",
    body: "We produce structured paths on inaraX: modules, check-ins, and quizzes mapped to the outcomes you scoped—pacing and placement match the cohort, not a one-size shelf course. The content and progression are personalized to each and every user.",
    insight:
      "The program stays one coherent contract for your org: required assessments and roll-up of scores, while every learner’s experience still adapts to them individually.",
    icon: BookOpen,
  },
  {
    key: 2,
    label: "Monitor",
    summary: "One dashboard: progress, completion, and quiz data.",
    title: "Users learn, you watch progress in the dashboard",
    body: "Admins and leaders use one view for starts, progress, lags, and quiz results—enough to report internally without building parallel spreadsheets.",
    insight: "Cohort-level context helps you nudge early and answer leadership with the same fields as in the app: participation, completion, and assessment scores.",
    icon: LayoutDashboard,
  },
] as const;

export default function B2BHowItWorks() {
  const reduce = useReducedMotion() ?? false;
  const [active, setActive] = useState(0);
  const tabId = useId();
  const labelId = useId();

  const go = useCallback((dir: -1 | 1) => {
    setActive((i) => (i + dir + STEPS.length) % STEPS.length);
  }, []);

  const select = useCallback((i: number) => {
    setActive(i);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(-1);
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(1);
    }
    if (e.key === "Home") {
      e.preventDefault();
      select(0);
    }
    if (e.key === "End") {
      e.preventDefault();
      select(STEPS.length - 1);
    }
  };

  const current = STEPS[active]!;
  const CurrentIcon = current.icon;

  return (
    <>
      <h2
        className="text-center text-3xl font-black tracking-tight text-stone-900 md:text-4xl lg:text-[2.5rem] lg:leading-tight"
        id={labelId}
      >
        How it works
      </h2>
      <p className="mx-auto mt-4 max-w-3xl text-center text-base leading-relaxed text-stone-600 md:mt-5 md:text-lg">
        Org subscribes → we deliver your course on inaraX → your people learn → you track progress and quiz results in the
        dashboard.
      </p>

      <p className="sr-only" aria-live="polite">
        Step {active + 1} of {STEPS.length}: {current.title}. Choose a step below; use left and right arrows when this block is
        focused.
      </p>

      <div
        className="mx-auto mt-7 max-w-6xl md:mt-9"
        onKeyDown={onKeyDown}
        role="group"
        aria-labelledby={labelId}
        tabIndex={0}
      >
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-stretch sm:gap-4"
          role="tablist"
          aria-label="Engagement steps"
        >
          {STEPS.map((s, i) => {
            const SIcon = s.icon;
            const isOn = active === i;
            return (
              <motion.button
                key={s.key}
                type="button"
                role="tab"
                id={`${tabId}-tab-${i}`}
                aria-selected={isOn}
                aria-controls={`${tabId}-panel`}
                tabIndex={isOn ? 0 : -1}
                onClick={() => select(i)}
                className={[
                  "flex h-full w-full min-h-0 min-w-0 flex-col items-center rounded-2xl border bg-white px-4 py-4 text-center",
                  "outline-none transition-[border-color,box-shadow] focus-visible:ring-2 focus-visible:ring-[#0050EF]/40 focus-visible:ring-offset-1",
                  isOn ? "border-stone-900/90 shadow-sm" : "border-stone-200/90 hover:border-stone-300",
                ].join(" ")}
                whileHover={reduce ? undefined : { y: -2, transition: { duration: 0.2, ease } }}
                whileTap={reduce ? undefined : { scale: 0.99 }}
                layout
              >
                <span
                  className={[
                    "flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white",
                    isOn ? "bg-stone-900" : "bg-stone-800",
                  ].join(" ")}
                >
                  <SIcon className="h-5 w-5" strokeWidth={2} aria-hidden />
                </span>
                <span className="mt-2.5 text-xs font-bold uppercase tracking-wider text-stone-500">{s.label}</span>
                <span className="mt-2 text-center text-sm leading-snug text-stone-800">{s.summary}</span>
              </motion.button>
            );
          })}
        </div>

        <div
          id={`${tabId}-panel`}
          role="tabpanel"
          aria-labelledby={`${tabId}-tab-${active}`}
          className="mt-5 flex min-h-[23.5rem] flex-col rounded-2xl border border-stone-200/80 bg-white p-5 sm:mt-6 sm:min-h-[22.5rem] sm:p-7 md:min-h-[21.5rem]"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={reduce ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease }}
              className="flex min-h-0 flex-1 flex-col gap-5 sm:flex-row sm:items-start sm:gap-6"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-stone-100 text-stone-900 sm:h-14 sm:w-14">
                <CurrentIcon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.5} aria-hidden />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <h3 className="text-lg font-bold text-stone-900 sm:text-xl md:text-2xl">{current.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-stone-600 sm:leading-relaxed md:text-[17px] md:leading-relaxed">
                  {current.body}
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700 sm:mt-5 md:text-[17px]">{current.insight}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
